import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../../../components/admin/AdminSidebar';
import AdminHeader from '../../../components/admin/AdminHeader';
import LocationForm from '../../../components/admin/LocationForm';
import { buildApiUrl, toUploadUrl } from '../../../config/api';

const AdminCampingLocations = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to perform this action');
            window.location.href = '/admin/login';
            return null;
        }
        return token;
    };

    const fetchLocations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = getToken();
            const response = await axios.get(buildApiUrl('/camping/locations'), {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (response.data.success) {
                setLocations(response.data.data || []);
            } else {
                setLocations([]);
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
                setTimeout(() => {
                    localStorage.removeItem('token');
                    window.location.href = '/admin/login';
                }, 2000);
            } else {
                setError('Failed to load locations');
            }
            setLocations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this location?')) return;
        
        try {
            const token = getToken();
            if (!token) return;
            
            await axios.delete(buildApiUrl(`/camping/admin/locations/${id}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            alert('✅ Location deleted successfully');
            fetchLocations();
        } catch (error) {
            console.error('Error deleting location:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert(error.response?.data?.message || 'Failed to delete location');
            }
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http')) return imagePath;
        return toUploadUrl(imagePath);
    };

    const getStatusBadgeClass = (status) => {
        return status === 'active' ? 'badge-success' : 'badge-warning';
    };

    const filteredLocations = locations.filter(loc => 
        loc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Camping Locations Management" />
                
                <div className="admin-camping-locations">
                    <div className="section-header">
                        <h2>
                            <i className="fas fa-map-marker-alt"></i>
                            Camping Locations
                            <span className="location-count">({filteredLocations.length})</span>
                        </h2>
                        <div className="header-actions">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by name, location or city..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className="clear-search" onClick={() => setSearchTerm('')}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                            <button 
                                className="btn-primary"
                                onClick={() => {
                                    setEditingLocation(null);
                                    setShowForm(true);
                                }}
                            >
                                <i className="fas fa-plus"></i> Add Location
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            <span>{error}</span>
                            <button onClick={fetchLocations} className="retry-btn">
                                <i className="fas fa-sync-alt"></i> Retry
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading locations...</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="locations-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Image</th>
                                        <th>Name</th>
                                        <th>Location</th>
                                        <th>City/State</th>
                                        <th>Sites</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLocations.length > 0 ? (
                                        filteredLocations.map(loc => (
                                            <tr key={loc.id}>
                                                <td className="id-column">#{loc.id}</td>
                                                <td className="image-column">
                                                    {loc.image ? (
                                                        <img 
                                                            src={getImageUrl(loc.image)} 
                                                            alt={loc.name}
                                                            className="location-thumb"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div className="no-image-thumb" style={{ display: loc.image ? 'none' : 'flex' }}>
                                                        <i className="fas fa-image"></i>
                                                    </div>
                                                </td>
                                                <td>
                                                    <strong>{loc.name}</strong>
                                                    {loc.slug && <div className="slug-text">{loc.slug}</div>}
                                                </td>
                                                <td className="address-cell">{loc.location}</td>
                                                <td>{loc.city}, {loc.state}</td>
                                                <td>
                                                    <span className="sites-badge">
                                                        <i className="fas fa-campground"></i> {loc.sites_count || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${getStatusBadgeClass(loc.status)}`}>
                                                        {loc.status || 'active'}
                                                    </span>
                                                </td>
                                                <td className="actions-cell">
                                                    <button 
                                                        className="btn-icon edit"
                                                        onClick={() => {
                                                            setEditingLocation(loc);
                                                            setShowForm(true);
                                                        }}
                                                        title="Edit Location"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button 
                                                        className="btn-icon delete"
                                                        onClick={() => handleDelete(loc.id)}
                                                        title="Delete Location"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="no-data">
                                                <div className="no-data-content">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                    <p>No locations found</p>
                                                    <button 
                                                        className="btn-primary"
                                                        onClick={() => {
                                                            setEditingLocation(null);
                                                            setShowForm(true);
                                                        }}
                                                    >
                                                        <i className="fas fa-plus"></i> Add Location
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {showForm && (
                        <LocationForm
                            location={editingLocation}
                            onClose={() => {
                                setShowForm(false);
                                setEditingLocation(null);
                            }}
                            onSuccess={() => {
                                fetchLocations();
                                setShowForm(false);
                                setEditingLocation(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

            <style>{`
               /* AdminCampingLocations.css */

/* Layout */
.admin-layout {
    display: flex;
    min-height: 100vh;
    background: #f8fafc;
}

.admin-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-left: 280px;
    background: #f8fafc;
}

.admin-camping-locations {
    padding: 2rem;
    background: #f8fafc;
    min-height: calc(100vh - 70px);
}

/* Section Header */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
    background: white;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    border: 1px solid #e2e8f0;
}

.section-header h2 {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.2rem;
    color: #1e293b;
    margin: 0;
}

.section-header h2 i {
    color: #2e7d32;
}

.location-count {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: normal;
    margin-left: 0.5rem;
}

.header-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
}

/* Search Box */
.search-box {
    display: flex;
    align-items: center;
    background: #f1f5f9;
    border-radius: 40px;
    padding: 0.5rem 1rem;
    width: 280px;
    position: relative;
}

.search-box i {
    color: #94a3b8;
    margin-right: 0.5rem;
}

.search-box input {
    background: none;
    border: none;
    width: 100%;
    font-size: 0.9rem;
    outline: none;
}

.clear-search {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 0;
    margin-left: 0.5rem;
}

.clear-search:hover {
    color: #d32f2f;
}

/* Buttons */
.btn-primary {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.5rem 1.2rem;
    border-radius: 40px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.btn-primary:hover {
    background: #1b5e20;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(46, 125, 50, 0.3);
}

/* Error Message */
.error-message {
    background: #fee2e2;
    color: #991b1b;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.error-message i {
    font-size: 1.1rem;
}

.error-message .retry-btn {
    margin-left: auto;
    background: #991b1b;
    color: white;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #2e7d32;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Table Container */
.table-container {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow-x: auto;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.locations-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
}

.locations-table th {
    text-align: left;
    padding: 1rem;
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e2e8f0;
}

.locations-table td {
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
    color: #1e293b;
    vertical-align: middle;
}

.locations-table tr:hover td {
    background: #f8fafc;
}

/* ID Column */
.id-column {
    font-weight: 500;
    color: #64748b;
    font-size: 0.85rem;
}

/* Image Column */
.image-column {
    width: 60px;
}

.location-thumb {
    width: 45px;
    height: 45px;
    border-radius: 8px;
    object-fit: cover;
    background: #f1f5f9;
}

.no-image-thumb {
    width: 45px;
    height: 45px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
}

.no-image-thumb i {
    font-size: 1.2rem;
}

/* Slug Text */
.slug-text {
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 0.2rem;
}

/* Address Cell */
.address-cell {
    max-width: 250px;
    white-space: normal;
    word-break: break-word;
    font-size: 0.85rem;
    color: #475569;
}

/* Sites Badge */
.sites-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: #f1f5f9;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #475569;
}

.sites-badge i {
    color: #2e7d32;
    font-size: 0.7rem;
}

/* Status Badge */
.status-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
}

.badge-success {
    background: #dcfce7;
    color: #166534;
}

.badge-warning {
    background: #fef9c3;
    color: #854d0e;
}

/* Actions Cell */
.actions-cell {
    white-space: nowrap;
}

.btn-icon {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.25rem;
    transition: all 0.2s;
}

.btn-icon:hover {
    transform: translateY(-1px);
}

.btn-icon.edit:hover {
    background: #e3f2fd;
    color: #1976d2;
}

.btn-icon.delete:hover {
    background: #fee2e2;
    color: #d32f2f;
}

/* No Data */
.no-data {
    text-align: center;
    padding: 3rem !important;
}

.no-data-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.no-data-content i {
    font-size: 3rem;
    color: #cbd5e1;
}

.no-data-content p {
    color: #64748b;
    margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
    }
    
    .admin-camping-locations {
        padding: 1rem;
    }
}

@media (max-width: 768px) {
    .section-header {
        flex-direction: column;
        align-items: flex-start;
        padding: 1rem;
    }
    
    .header-actions {
        width: 100%;
        flex-direction: column;
    }
    
    .search-box {
        width: 100%;
    }
    
    .btn-primary {
        width: 100%;
        justify-content: center;
    }
    
    .table-container {
        border-radius: 8px;
    }
    
    .locations-table th,
    .locations-table td {
        padding: 0.75rem;
    }
}
            `}</style>
export default AdminCampingLocations;
